import type { Pt } from "@tasduvar/engine";
import type { DimLine } from "./geometry";

const TOL = 1e-6;
const r3 = (v: number): number => Math.round(v * 1000) / 1000;
const fmtM = (v: number): string => v.toFixed(2).replace(".", ",");

/**
 * Çokgen kesitin kenar ölçüleri (anahtar `e{i}.x` = i. kenarın yatay izdüşümü, `e{i}.y` = düşey izdüşümü).
 * Ölçü çokgenin dışına konur (CCW → dış normal sağda). Taban kenarı (B) ve tam yükseklikteki eğik kenar düşeyi (H) yinelenmez.
 */
export function polygonEdgeDims(points: Pt[]): DimLine[] {
  const n = points.length;
  const ys = points.map((p) => p.y);
  const baseY = Math.min(...ys), yTop = Math.max(...ys);
  const dims: DimLine[] = [];
  let area2 = 0;
  for (let i = 0; i < n; i++) { const p = points[i]!, q = points[(i + 1) % n]!; area2 += p.x * q.y - q.x * p.y; }
  const ccw = area2 >= 0 ? 1 : -1;
  for (let i = 0; i < n; i++) {
    const a = points[i]!, b = points[(i + 1) % n]!;
    const dx = b.x - a.x, dy = b.y - a.y;
    const horizontal = Math.abs(dy) < TOL, vertical = Math.abs(dx) < TOL;
    // Dış normal (CCW): (dy, -dx) — yatay kenarda düşey işaret, düşey kenarda yatay işaret (CW çokgende ters)
    const outY = ccw * Math.sign(-dx), outX = ccw * Math.sign(dy);
    if (horizontal) {
      if (Math.abs(a.y - baseY) < TOL) continue;                                   // taban = B
      dims.push({ key: `e${i}.x`, a, b, label: `${fmtM(Math.abs(dx))} m`, offset: 0.35 * outY });
      continue;
    }
    if (vertical) { dims.push({ key: `e${i}.y`, a, b, label: `${fmtM(Math.abs(dy))} m`, offset: 0.45 * outX }); continue; }
    // Eğik kenar: yatay izdüşüm üst uç kotunda (dışarıda kalan taraf), düşey izdüşüm dış x'te
    const up = dy > 0 ? b : a, low = dy > 0 ? a : b;
    dims.push({ key: `e${i}.x`, a: { x: Math.min(a.x, b.x), y: up.y }, b: { x: Math.max(a.x, b.x), y: up.y }, label: `${fmtM(Math.abs(dx))} m`, offset: Math.abs(up.y - yTop) < TOL ? 0.35 : 0.3 * (outY || 1) });
    if (Math.abs(Math.abs(dy) - (yTop - baseY)) > TOL) {
      const xOut = outX > 0 ? Math.max(a.x, b.x) : Math.min(a.x, b.x);
      dims.push({ key: `e${i}.y`, a: { x: xOut, y: low.y }, b: { x: xOut, y: up.y }, label: `${fmtM(Math.abs(dy))} m`, offset: 0.45 * (outX || 1) });
    }
  }
  return dims;
}

/**
 * Kenar ölçüsünü uygular (AutoCAD STRETCH mantığı): eksen boyunca büyük koordinatlı uç (arka / üst) taşınır, öbürü sabit;
 * taşınan uçtan düzenlenen kenarın tersine gidilerek eksene DİK kenarların uzak uçları da aynı miktarda ötelenir
 * (dikdörtgen temel dikdörtgen kalır); dik olmayan ilk kenar farkı yutar. `B` taban kenarını, `H` tüm kotları oransal ölçekler.
 */
export function applyPolygonDimension(points: Pt[], key: string, value: number): Pt[] {
  const n = points.length;
  const ys = points.map((p) => p.y);
  const baseY = Math.min(...ys), yTop = Math.max(...ys);
  if (key === "H") { const k = Math.max(0.05, value) / (yTop - baseY); return points.map((p) => ({ x: p.x, y: r3(baseY + (p.y - baseY) * k) })); }
  let i: number, axis: "x" | "y";
  if (key === "B") {
    const base = points.map((p, k) => ({ p, k })).filter(({ p }) => Math.abs(p.y - baseY) < TOL);
    if (base.length !== 2) return points;
    const toe = base.reduce((u, v) => (v.p.x < u.p.x ? v : u)), heel = base.reduce((u, v) => (v.p.x > u.p.x ? v : u));
    i = (toe.k + 1) % n === heel.k ? toe.k : heel.k; axis = "x";
  } else {
    const m = /^e(\d+)\.(x|y)$/.exec(key); if (!m) return points;
    i = Number(m[1]); axis = m[2] as "x" | "y";
    if (i >= n) return points;
  }
  const v = Math.max(axis === "y" || key === "B" ? 0.05 : 0, value);
  const ia = i, ib = (i + 1) % n;
  const a = points[ia]!, b = points[ib]!;
  const cur = Math.abs(b[axis] - a[axis]);
  const movingB = b[axis] >= a[axis];
  const delta = v - cur;                                                           // taşınan uç hep büyük koordinatlı olan → fark eksen yönünde
  const moved = new Set<number>([movingB ? ib : ia]);
  const step = movingB ? 1 : -1;
  let cur_i = movingB ? ib : ia;
  for (let k = 0; k < n - 2; k++) {
    const next = (cur_i + step + n) % n;
    if (next === (movingB ? ia : ib)) break;
    const p = points[cur_i]!, q = points[next]!;
    if (Math.abs(q[axis] - p[axis]) > TOL) break;                                  // eksene dik değil → farkı yutar
    moved.add(next); cur_i = next;
  }
  return points.map((p, k) => (moved.has(k) ? { ...p, [axis]: r3(p[axis] + delta) } : p));
}
